/** @typedef {import('@/types/structured-data-editor').SdeDraftEnvelope} SdeDraftEnvelope */

export const DRAFT_VERSION = 1;
const DRAFT_TTL_DAYS = 7;
const IDB_NAME = "safetee_sde_drafts";
const IDB_STORE = "drafts";
const IDB_VERSION = 1;

/** @type {Map<string, string>} */
const memoryStore = new Map();

/**
 * @param {number|string} entityId
 * @param {number|string} userId
 * @param {string} draftId
 */
export function draftStorageKey(entityId, userId, draftId) {
    return `sde_draft_v${DRAFT_VERSION}_${entityId}_${userId}_${draftId}`;
}

/**
 * @param {Date} date
 * @param {number} days
 */
function addDays(date, days) {
    const copy = new Date(date);
    copy.setDate(copy.getDate() + days);
    return copy;
}

/**
 * @returns {Promise<IDBDatabase|null>}
 */
function openDraftDb() {
    if (typeof indexedDB === "undefined") {
        return Promise.resolve(null);
    }
    return new Promise((resolve, reject) => {
        const request = indexedDB.open(IDB_NAME, IDB_VERSION);
        request.onupgradeneeded = () => {
            const db = request.result;
            if (!db.objectStoreNames.contains(IDB_STORE)) {
                db.createObjectStore(IDB_STORE);
            }
        };
        request.onsuccess = () => resolve(request.result);
        request.onerror = () => reject(request.error || new Error("IndexedDB open failed"));
    });
}

/**
 * @template T
 * @param {"readonly"|"readwrite"} mode
 * @param {(store: IDBObjectStore) => IDBRequest} run
 * @returns {Promise<T|undefined>}
 */
async function withDraftStore(mode, run) {
    const db = await openDraftDb();
    if (!db) {
        return undefined;
    }
    return new Promise((resolve, reject) => {
        const tx = db.transaction(IDB_STORE, mode);
        const store = tx.objectStore(IDB_STORE);
        const request = run(store);
        request.onsuccess = () => resolve(/** @type {T} */ (request.result));
        request.onerror = () => reject(request.error || new Error("IndexedDB request failed"));
    });
}

/**
 * @param {string} key
 * @returns {string|null}
 */
function readLegacyLocalStorage(key) {
    if (typeof localStorage === "undefined") {
        return null;
    }
    try {
        return localStorage.getItem(key);
    } catch {
        return null;
    }
}

/**
 * @param {string} key
 */
function removeLegacyLocalStorage(key) {
    if (typeof localStorage === "undefined") {
        return;
    }
    try {
        localStorage.removeItem(key);
    } catch {
        // ignore quota / access errors while cleaning up
    }
}

/**
 * @param {string} key
 * @param {string} raw
 */
async function writeRawDraft(key, raw) {
    const db = await openDraftDb();
    if (!db) {
        memoryStore.set(key, raw);
        return;
    }
    await withDraftStore("readwrite", (store) => store.put(raw, key));
}

/**
 * @param {string} key
 * @returns {Promise<string|null>}
 */
async function readRawDraft(key) {
    const db = await openDraftDb();
    if (!db) {
        if (memoryStore.has(key)) {
            return memoryStore.get(key) || null;
        }
        const legacyMemory = readLegacyLocalStorage(key);
        if (legacyMemory) {
            memoryStore.set(key, legacyMemory);
            removeLegacyLocalStorage(key);
            return legacyMemory;
        }
        return null;
    }
    const fromIdb = await withDraftStore("readonly", (store) => store.get(key));
    if (typeof fromIdb === "string" && fromIdb) {
        return fromIdb;
    }
    const legacy = readLegacyLocalStorage(key);
    if (legacy) {
        try {
            await writeRawDraft(key, legacy);
            removeLegacyLocalStorage(key);
        } catch {
            // keep legacy value readable even if migration fails
        }
        return legacy;
    }
    return null;
}

/**
 * @param {string} key
 */
async function deleteRawDraft(key) {
    const db = await openDraftDb();
    if (!db) {
        memoryStore.delete(key);
        removeLegacyLocalStorage(key);
        return;
    }
    await withDraftStore("readwrite", (store) => store.delete(key));
    removeLegacyLocalStorage(key);
}

/**
 * @param {SdeDraftEnvelope} envelope
 */
export async function saveDraft(envelope) {
    const key = draftStorageKey(envelope.entityId, envelope.userId, envelope.draftId);
    const payload = {
        ...envelope,
        version: DRAFT_VERSION,
        savedAt: new Date().toISOString(),
        expiresAt: addDays(new Date(), DRAFT_TTL_DAYS).toISOString(),
    };
    await writeRawDraft(key, JSON.stringify(payload));
    removeLegacyLocalStorage(key);
}

/**
 * @param {number|string} entityId
 * @param {number|string} userId
 * @param {string} draftId
 * @returns {Promise<SdeDraftEnvelope|null>}
 */
export async function loadDraft(entityId, userId, draftId) {
    if (!entityId || !userId || !draftId) {
        return null;
    }
    const raw = await readRawDraft(draftStorageKey(entityId, userId, draftId));
    if (!raw) {
        return null;
    }
    try {
        const envelope = JSON.parse(raw);
        if (new Date(envelope.expiresAt) < new Date()) {
            await clearDraft(entityId, userId, draftId);
            return null;
        }
        return envelope;
    } catch {
        await clearDraft(entityId, userId, draftId);
        return null;
    }
}

/**
 * @param {number|string} entityId
 * @param {number|string} userId
 * @param {string} draftId
 */
export async function clearDraft(entityId, userId, draftId) {
    if (!entityId || !userId || !draftId) {
        return;
    }
    await deleteRawDraft(draftStorageKey(entityId, userId, draftId));
}

/**
 * @param {number|string} entityId
 * @param {number|string} userId
 * @param {string} sessionId
 */
export async function clearDraftForSession(entityId, userId, sessionId) {
    await clearDraft(entityId, userId, sessionId);
}

export function generateDraftId() {
    if (typeof crypto !== "undefined" && crypto.randomUUID) {
        return crypto.randomUUID();
    }
    return `draft-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

/**
 * @param {string} value
 */
function hashStableString(value) {
    let hash = 0;
    for (let index = 0; index < value.length; index += 1) {
        hash = (hash * 31 + value.charCodeAt(index)) | 0;
    }
    return Math.abs(hash);
}

/**
 * Stable draft key for bulk-edit modals so local edits survive modal close/reopen.
 *
 * @param {{
 *   syncType: string,
 *   providerId?: number|string|null,
 *   allProducts?: boolean,
 *   productIds?: number[],
 *   listFilters?: Record<string, unknown>|null,
 * }} options
 */
export function buildBulkDraftId({
    syncType,
    providerId = null,
    allProducts = false,
    productIds = [],
    listFilters = null,
}) {
    const providerPart = providerId != null ? String(providerId) : "0";
    if (allProducts) {
        return `bulk-${syncType}-${providerPart}-all`;
    }
    const sortedIds = [...(productIds || [])]
        .map(String)
        .sort((a, b) => Number(a) - Number(b));
    if (sortedIds.length) {
        if (sortedIds.length <= 24) {
            return `bulk-${syncType}-${providerPart}-ids-${sortedIds.join("_")}`;
        }
        const joined = sortedIds.join(",");
        return `bulk-${syncType}-${providerPart}-ids-h${hashStableString(joined)}-n${sortedIds.length}`;
    }
    if (listFilters && Object.keys(listFilters).length) {
        const keys = Object.keys(listFilters).sort();
        const joined = JSON.stringify(listFilters, keys);
        return `bulk-${syncType}-${providerPart}-filters-h${hashStableString(joined)}`;
    }
    return `bulk-${syncType}-${providerPart}-selection`;
}
